import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash, ImageSquare, ChatTeardropDots } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DetailingLandingEditor() {
  const { t } = useLanguage();
  const [cfg, setCfg] = useState({
    hero_title: '', hero_subtitle: '', bullets: [], protection_years: 3,
    ppf_enabled: true, ppf_text: '', testimonials: [], showcase_images: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/detailing/landing-config`);
      setCfg(r.data);
    } catch (e) { toast.error('Error'); }
    setLoading(false);
  };
  useEffect(() => { fetch(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/detailing/landing-config`, cfg, { withCredentials: true });
      toast.success('OK');
    } catch (e) { toast.error('Error'); }
    setSaving(false);
  };

  const updateBullet = (idx, value) => {
    const next = [...cfg.bullets]; next[idx] = value;
    setCfg({ ...cfg, bullets: next });
  };
  const addBullet = () => setCfg({ ...cfg, bullets: [...cfg.bullets, ''] });
  const removeBullet = (idx) => setCfg({ ...cfg, bullets: cfg.bullets.filter((_, i) => i !== idx) });

  const addImage = () => setCfg({ ...cfg, showcase_images: [...(cfg.showcase_images || []), ''] });
  const updateImage = (idx, value) => {
    const next = [...(cfg.showcase_images || [])]; next[idx] = value;
    setCfg({ ...cfg, showcase_images: next });
  };
  const removeImage = (idx) => setCfg({ ...cfg, showcase_images: cfg.showcase_images.filter((_, i) => i !== idx) });

  const addTestimonial = () => setCfg({ ...cfg, testimonials: [...(cfg.testimonials || []), { name: '', city: '', text: '' }] });
  const updateTestimonial = (idx, field, value) => {
    const next = [...(cfg.testimonials || [])];
    next[idx] = { ...(next[idx] || {}), [field]: value };
    setCfg({ ...cfg, testimonials: next });
  };
  const removeTestimonial = (idx) => setCfg({ ...cfg, testimonials: cfg.testimonials.filter((_, i) => i !== idx) });

  if (loading) return <p className="text-center text-white/40 text-sm py-6">…</p>;

  return (
    <div className="space-y-4" data-testid="detailing-landing-editor">
      {/* Hero */}
      <div className="p-3 rounded-2xl bg-[#0a0a0a] border border-white/5 space-y-2">
        <h4 className="text-xs uppercase tracking-wider text-white/55">Hero</h4>
        <Input value={cfg.hero_title} onChange={(e) => setCfg({ ...cfg, hero_title: e.target.value })}
               placeholder="Titulli" className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm"
               data-testid="landing-hero-title" />
        <Input value={cfg.hero_subtitle} onChange={(e) => setCfg({ ...cfg, hero_subtitle: e.target.value })}
               placeholder="Nëntitulli" className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm"
               data-testid="landing-hero-subtitle" />
      </div>

      {/* Protection years */}
      <div className="p-3 rounded-2xl bg-[#0a0a0a] border border-white/5">
        <h4 className="text-xs uppercase tracking-wider text-white/55 mb-2">Mbrojtja (vjet)</h4>
        <Input type="number" min="1" max="5" value={cfg.protection_years || 3}
               onChange={(e) => setCfg({ ...cfg, protection_years: Math.max(1, Math.min(5, parseInt(e.target.value) || 3)) })}
               className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm w-24"
               data-testid="landing-protection-years" />
        <p className="text-[10px] text-white/40 mt-1">Përdoret automatikisht në bullet &quot;Mbrojtje deri në X vjet&quot;</p>
      </div>

      {/* Bullets */}
      <div className="p-3 rounded-2xl bg-[#0a0a0a] border border-white/5">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs uppercase tracking-wider text-white/55">Bullet (benefice)</h4>
          <Button size="sm" onClick={addBullet}
                  className="h-7 rounded-lg bg-[#0ABAB5] text-black text-[10px]" data-testid="landing-bullet-add">
            <Plus size={10} className="mr-1" /> Shto
          </Button>
        </div>
        <div className="space-y-2">
          {(cfg.bullets || []).map((b, i) => (
            <div key={i} className="flex gap-2" data-testid={`landing-bullet-${i}`}>
              <Input value={b} onChange={(e) => updateBullet(i, e.target.value)}
                     className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm flex-1" />
              <button onClick={() => removeBullet(i)} className="w-9 h-9 rounded-xl bg-red-500/10 text-red-400">
                <Trash size={12} weight="fill" className="mx-auto" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* PPF toggle + text */}
      <div className="p-3 rounded-2xl bg-[#0a0a0a] border border-white/5">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs uppercase tracking-wider text-white/55">Krahasim PPF</h4>
          <button onClick={() => setCfg({ ...cfg, ppf_enabled: !cfg.ppf_enabled })}
                  data-testid="landing-ppf-toggle"
                  className={`relative w-10 h-5 rounded-full ${cfg.ppf_enabled ? 'bg-[#0ABAB5]' : 'bg-white/15'}`}>
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition ${cfg.ppf_enabled ? 'translate-x-5' : ''}`} />
          </button>
        </div>
        {cfg.ppf_enabled && (
          <Input value={cfg.ppf_text || ''} onChange={(e) => setCfg({ ...cfg, ppf_text: e.target.value })}
                 placeholder="PPF tradicional kushton 3.000-5.000€…"
                 className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm"
                 data-testid="landing-ppf-text" />
        )}
      </div>

      {/* Showcase images */}
      <div className="p-3 rounded-2xl bg-[#0a0a0a] border border-white/5">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs uppercase tracking-wider text-white/55 flex items-center gap-1">
            <ImageSquare size={12} /> Foto showcase (URL)
          </h4>
          <Button size="sm" onClick={addImage}
                  className="h-7 rounded-lg bg-[#0ABAB5] text-black text-[10px]" data-testid="landing-image-add">
            <Plus size={10} className="mr-1" /> Shto
          </Button>
        </div>
        <div className="space-y-2">
          {(cfg.showcase_images || []).map((url, i) => (
            <div key={i} className="flex gap-2" data-testid={`landing-image-${i}`}>
              <Input value={url} onChange={(e) => updateImage(i, e.target.value)}
                     placeholder="https://… (Unsplash, S3, etj)"
                     className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm flex-1 font-mono text-[10px]" />
              <button onClick={() => removeImage(i)} className="w-9 h-9 rounded-xl bg-red-500/10 text-red-400">
                <Trash size={12} weight="fill" className="mx-auto" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Testimonials */}
      <div className="p-3 rounded-2xl bg-[#0a0a0a] border border-white/5">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs uppercase tracking-wider text-white/55 flex items-center gap-1">
            <ChatTeardropDots size={12} /> Testimoniale
          </h4>
          <Button size="sm" onClick={addTestimonial}
                  className="h-7 rounded-lg bg-[#0ABAB5] text-black text-[10px]" data-testid="landing-testimonial-add">
            <Plus size={10} className="mr-1" /> Shto
          </Button>
        </div>
        <div className="space-y-3">
          {(cfg.testimonials || []).map((t, i) => (
            <div key={i} className="p-2 rounded-xl bg-[#050505] border border-white/5 space-y-1.5"
                 data-testid={`landing-testimonial-${i}`}>
              <div className="grid grid-cols-2 gap-2">
                <Input value={t.name || ''} onChange={(e) => updateTestimonial(i, 'name', e.target.value)}
                       placeholder="Emër" className="h-8 bg-[#0a0a0a] border-white/10 rounded-lg text-xs" />
                <Input value={t.city || ''} onChange={(e) => updateTestimonial(i, 'city', e.target.value)}
                       placeholder="Qytet" className="h-8 bg-[#0a0a0a] border-white/10 rounded-lg text-xs" />
              </div>
              <Input value={t.text || ''} onChange={(e) => updateTestimonial(i, 'text', e.target.value)}
                     placeholder="Komenti" className="h-8 bg-[#0a0a0a] border-white/10 rounded-lg text-xs" />
              <button onClick={() => removeTestimonial(i)} className="text-[10px] text-red-400">
                Fshi
              </button>
            </div>
          ))}
        </div>
      </div>

      <Button onClick={save} disabled={saving} data-testid="landing-save-btn"
              className="w-full bg-[#FF5A00] hover:bg-[#FF5A00]/90 text-white h-11 rounded-xl">
        {saving ? '…' : 'Ruaj ndryshimet'}
      </Button>
    </div>
  );
}
